import { useEffect, useState, type ReactNode, type Ref } from 'react'
import { FormattedMessage } from 'react-intl'
import { Platform, View, type TextInput } from 'react-native'

import { Button, type TDefaultButtonProps } from '@/src/elements/button/default'
import { Textarea, type TTextareaProps } from '@/src/elements/input/Textarea'
import { Message } from '@/src/elements/message'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'
import type { TWithBrowserTextareaHeightProps } from '@/src/general/utils/withBrowserTextareaHeight'

import { IntentionCtaBlock } from './IntentionCtaBlock'

export type TIntentionFormCtaBlockProps = {
  button?: Omit<TDefaultButtonProps, 'onPress'>
  title: string | JSX.Element | ReactNode
  description?: string | JSX.Element | ReactNode
  successMessage?: string | JSX.Element | ReactNode
  error: Error | null
  isPending: boolean
  isSuccess: boolean
  onDisplay?: VoidFunction
  onSubmit: (content: string) => void
  contentFieldName?: string
  ref?: Ref<TextInput>
} & Pick<TTextareaProps, 'testID' | 'maxLength' | 'numberOfLines' | 'placeholder'> &
  Partial<TWithBrowserTextareaHeightProps>

export const IntentionFormCtaBlock = ({
  testID,
  title,
  contentFieldName = 'content',
  description,
  successMessage,
  error,
  button,
  onDisplay,
  onSubmit,
  onValueUpdate,
  isPending,
  isSuccess,
  ref,
  ...textareaProps
}: TIntentionFormCtaBlockProps): ReactNode => {
  const [content, setContent] = useState<string>('')

  useEffect(() => onDisplay?.(), [onDisplay])

  if (isSuccess) {
    return (
      <IntentionCtaBlock isSuccess>
        <Typography colors="success" contrast="high" bold size="lg">
          {title}
        </Typography>

        <Message colors="success">{content}</Message>

        {successMessage && (
          <Message iconName={ICON.SEND} colors="success">
            {successMessage}
          </Message>
        )}
      </IntentionCtaBlock>
    )
  }

  return (
    <IntentionCtaBlock>
      <Typography colors="primary" contrast="high" bold size="lg">
        {title}
      </Typography>
      {description && (
        <Typography colors="primary" contrast="high">
          {description}
        </Typography>
      )}
      <Textarea
        ref={ref}
        testID={testID ?? TEST_ID.INTENTION.FORM.INPUT}
        onChange={(event) => {
          setContent(event.nativeEvent.text)
          onValueUpdate?.()
        }}
        color="primary"
        classNames={{ input: 'w-full min-h-11 pt-0' }}
        numberOfLines={Platform.OS === 'web' ? 2 : undefined}
        {...textareaProps}
      />
      {error && (
        <Message colors="danger">
          <FormattedMessage id={parseApiError(error, contentFieldName) ?? 'common.error.unknown'} />
        </Message>
      )}
      {content.length > 0 && (
        <View className="flex w-full flex-row justify-end">
          <Button variant="solid" colors="primary" onPress={() => onSubmit(content)} isDisabled={isPending} {...button}>
            {button?.children ?? <FormattedMessage id="word.send" />}
          </Button>
        </View>
      )}
    </IntentionCtaBlock>
  )
}
